import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ShieldCheck, ChevronDown, ChevronUp } from 'lucide-react';
import SEO from '../components/SEO';

const Accreditations = () => {
  const [openFaqIndex, setOpenFaqIndex] = React.useState(null);

  const counties = [
    "Bedfordshire", "Berkshire", "Bristol", "Buckinghamshire", "Cambridgeshire", "Cheshire", "City of London", "Cornwall", "Cumbria", "Derbyshire", "Devon", "Dorset", "County Durham", "East Riding of Yorkshire", "East Sussex", "Essex", "Gloucestershire", "Greater London", "Greater Manchester", "Hampshire", "Herefordshire", "Hertfordshire", "Isle of Wight", "Kent", "Lancashire", "Leicestershire", "Lincolnshire", "Merseyside", "Norfolk", "North Yorkshire", "Northamptonshire", "Northumberland", "Nottinghamshire", "Oxfordshire", "Rutland", "Shropshire", "Somerset", "South Yorkshire", "Staffordshire", "Suffolk", "Surrey", "Tyne and Wear", "Warwickshire", "West Midlands", "West Sussex", "West Yorkshire", "Wiltshire", "Worcestershire",
    "Blaenau Gwent", "Bridgend", "Caerphilly", "Cardiff", "Carmarthenshire", "Ceredigion", "Conwy", "Denbighshire", "Flintshire", "Gwynedd", "Isle of Anglesey", "Merthyr Tydfil", "Monmouthshire", "Neath Port Talbot", "Newport", "Pembrokeshire", "Powys", "Rhondda Cynon Taf", "Swansea", "Torfaen", "Vale of Glamorgan", "Wrexham"
  ];

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Green Frog Utilities",
    "legalName": "GREEN FROG UTILITIES LIMITED",
    "foundingDate": "2014",
    "url": "https://greenfrogutilities.co.uk",
    "subOrganization": {
      "@type": "Organization",
      "name": "Green Frog Asset Management",
      "url": "https://greenfrogassetmanagement.co.uk"
    },
    "hasCredential": [
      { "@type": "EducationalOccupationalCredential", "name": "GIRS, Gas Industry Registration Scheme" },
      { "@type": "EducationalOccupationalCredential", "name": "WIRS, Water Industry Registration Scheme" },
      { "@type": "EducationalOccupationalCredential", "name": "NERS, National Electricity Registration Scheme" },
      { "@type": "EducationalOccupationalCredential", "name": "MAM, Meter Asset Manager" },
      { "@type": "EducationalOccupationalCredential", "name": "AMI, Approved Meter Installer" },
      { "@type": "EducationalOccupationalCredential", "name": "ISO Management Systems" },
      { "@type": "EducationalOccupationalCredential", "name": "Constructionline Gold" },
      { "@type": "EducationalOccupationalCredential", "name": "Achilles UVDB Silver Plus" },
      { "@type": "EducationalOccupationalCredential", "name": "SSIP" },
      { "@type": "EducationalOccupationalCredential", "name": "SafeContractor" },
      { "@type": "EducationalOccupationalCredential", "name": "Gas Safe Registered" },
      { "@type": "EducationalOccupationalCredential", "name": "IGEM Member" },
      { "@type": "EducationalOccupationalCredential", "name": "Cyber Essentials" },
      { "@type": "EducationalOccupationalCredential", "name": "CoMCoP Accredited" }
    ]
  };

  const faqData = [
    {
      question: "What is the difference between GIRS, WIRS and NERS?",
      answer: "They are the three network accreditations, one for each utility. GIRS covers gas mains and services, WIRS covers water mains and services, and NERS covers electrical connections. Each allows the accredited contractor to build assets that the network operator then adopts. Green Frog Utilities holds all three."
    },
    {
      question: "Who audits Green Frog Utilities' accreditations?",
      answer: "The three network schemes are audited by LRQA. Management systems are certified by ISOQAR under UKAS. Supply chain and safety schemes are Constructionline, Achilles UVDB, SSIP and SafeContractor."
    },
    {
      question: "Do I need an accredited contractor for a utility connection?",
      answer: "For any connection that joins the public network, yes. The network operator will only adopt work carried out by a contractor registered under the relevant scheme."
    },
    {
      question: "Can one accredited contractor do gas, water and electric together?",
      answer: "Yes, if it holds all three schemes. Green Frog Utilities holds GIRS, WIRS and NERS, so all three connections can be designed and built by one contractor on one programme."
    },
    {
      question: "Are the metering accreditations separate?",
      answer: "Yes. Meter installation and management sit under MAM and AMI, which are held by Green Frog Asset Management, the metering company in the Green Frog group."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://greenfrogutilities.co.uk/" },
      { "@type": "ListItem", "position": 2, "name": "Accreditations", "item": "https://greenfrogutilities.co.uk/accreditations" }
    ]
  };

  const networkAccreditations = [
    {
      image: "https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/7dxbfb2l_GIRS-accredited.png",
      alt: "GIRS accredited gas contractor, LRQA",
      title: "GIRS, Gas Industry Registration Scheme",
      auditor: "Audited by LRQA",
      desc: "Allows Green Frog Utilities to design and construct gas mains and services that become part of the public gas network.",
      link: "/services/connections",
      linkText: "See our gas connections page"
    },
    {
      image: "https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/wljohupy_WIRS-accredited.png",
      alt: "WIRS accredited water contractor, LRQA",
      title: "WIRS, Water Industry Registration Scheme",
      auditor: "Audited by LRQA",
      desc: "Allows Green Frog Utilities to design and lay water mains and services as a self lay provider, adopted by the water company.",
      link: "/water-connections",
      linkText: "See our water connections page"
    },
    {
      image: "https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/rqw3vpgj_NERS-accredited.png",
      alt: "NERS accredited electrical contractor, LRQA",
      title: "NERS, National Electricity Registration Scheme",
      auditor: "Audited by LRQA",
      desc: "Allows Green Frog Utilities to design and build electrical connections as an independent connection provider, adopted by the distribution network.",
      link: "/electrical-connections",
      linkText: "See our electrical connections page"
    }
  ];

  const qualityAccreditations = [
    {
      image: "https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/or7bnh4g_01-ISOQAR-UKAS.png",
      alt: "ISOQAR Registered and UKAS Management Systems",
      title: "ISOQAR and UKAS",
      desc: "Management systems certified by ISOQAR under UKAS accreditation."
    },
    {
      image: "https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/mmnow9bf_02-ConstructionLine-Gold.jpg",
      alt: "Constructionline Gold member",
      title: "Constructionline Gold",
      desc: "Pre-qualified supplier status used by main contractors and public sector buyers."
    },
    {
      image: "https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/mlz8i6t2_03-Achilles-UVDB-SilverPlus.jpg",
      alt: "Achilles UVDB Silver Plus",
      title: "Achilles UVDB Silver Plus",
      desc: "Utilities supply chain verification, including an on site audit."
    },
    {
      image: "https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/6v1vokrm_09-SSIP.jpg",
      alt: "SSIP assessed",
      title: "SSIP",
      desc: "Safety Schemes in Procurement, health and safety assessed."
    },
    {
      image: "https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/0obf2zin_10-SafeContractor.png",
      alt: "SafeContractor approved",
      title: "SafeContractor",
      desc: "Health and safety accreditation for contractors."
    },
    {
      image: "https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/h7w5jyk6_05-GasSafe.jpg",
      alt: "Gas Safe registered",
      title: "Gas Safe registered",
      desc: "Gas Safe registered."
    },
    {
      image: "https://customer-assets-jai6qajn.emergentagent.net/job_16d59296-5a5e-4ca3-aca8-4873a1016fa1/artifacts/s4lr6tj8_06-IGEM.png",
      alt: "IGEM member",
      title: "IGEM",
      desc: "Member of the Institution of Gas Engineers and Managers."
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div>
      <SEO 
        title="Accreditations | GIRS, WIRS, NERS and Industry Standards | Green Frog Utilities"
        description="Every accreditation Green Frog Utilities holds, what each one allows us to do, and who audits it. GIRS, WIRS and NERS through LRQA, ISO through ISOQAR and UKAS, Constructionline Gold, Achilles UVDB, Gas Safe, IGEM, Cyber Essentials and more."
        canonical="/accreditations"
        jsonLd={[organizationSchema, faqSchema, breadcrumbSchema]}
      />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-emerald-600 to-emerald-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <div className="flex justify-center mb-6">
              <ShieldCheck className="h-16 w-16" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Accreditations
            </h1>
            <p className="text-xl text-emerald-100 max-w-4xl mx-auto leading-relaxed">
              What each accreditation means, what it lets us do on your site, and who checks it.
            </p>
          </div>
        </div>
      </section>

      {/* Why Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why the accreditations matter
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              On a utility connection the accreditation is not a badge, it is the permission. Without GIRS a contractor cannot lay gas mains that join the public network. Without WIRS the water company will not adopt the main. Without NERS the distribution network will not connect the cable. Green Frog Utilities holds all three, audited by LRQA, alongside the safety, quality and supply chain accreditations that developers, main contractors and public bodies ask for at tender stage.
            </p>
          </div>
        </div>
      </section>

      {/* Network Accreditations Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            Network accreditations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {networkAccreditations.map((accred, index) => (
              <Card key={index} className="border-none hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-4">
                    <img 
                      src={accred.image} 
                      alt={accred.alt}
                      className="h-24 w-auto object-contain"
                    />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{accred.title}</h3>
                  <p className="text-sm text-emerald-600 mb-2">{accred.auditor}</p>
                  <p className="text-gray-600 mb-3">{accred.desc}</p>
                  <Link to={accred.link} className="text-emerald-600 hover:text-emerald-700 text-sm font-medium">
                    {accred.linkText} &rarr;
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Metering Accreditations Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            Metering accreditations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
            <Card className="border-none hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <p className="text-gray-600 mb-3">
                  Green Frog Asset Management, our metering company, holds MAM (Meter Asset Manager) and AMI (Approved Meter Installer) accreditation and Cyber Essentials.
                </p>
                <Link to="/metering" className="text-emerald-600 hover:text-emerald-700 text-sm font-medium">
                  See our metering page &rarr;
                </Link>
              </CardContent>
            </Card>
            <Card className="border-none hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex justify-center mb-3 h-16">
                  <img 
                    src="/images/comcop-accredited.png" 
                    alt="CoMCoP Accredited"
                    className="h-full w-auto object-contain"
                  />
                </div>
                <h3 className="font-semibold text-gray-900 text-sm mb-1">CoMCoP</h3>
                <p className="text-gray-600 text-sm">Accredited</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Quality, Safety and Supply Chain Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            Quality, safety and supply chain
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {qualityAccreditations.map((accred, index) => (
              <Card key={index} className="border-none hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="flex justify-center mb-3 h-16">
                    <img 
                      src={accred.image} 
                      alt={accred.alt}
                      className="h-full w-auto object-contain"
                    />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm mb-1">{accred.title}</h3>
                  <p className="text-gray-600 text-sm">{accred.desc}</p>
                </CardContent>
              </Card>
            ))}
            {/* Cyber Essentials iframe */}
            <Card className="border-none hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex justify-center mb-3">
                  <iframe 
                    src="https://registry.blockmarktech.com/certificates/c871dedf-9ab4-47d8-a7de-cd2f2de05cbb/widget/?tooltip_position=bottom&theme=transparent" 
                    style={{ border: 'none', height: '100px', width: '100px' }} 
                    loading="lazy"
                    title="Cyber Essentials Certified"
                  />
                </div>
                <h3 className="font-semibold text-gray-900 text-sm mb-1">Cyber Essentials</h3>
                <p className="text-gray-600 text-sm">Government backed cyber security certification.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            Frequently asked questions
          </h2>
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div 
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </span>
                  {openFaqIndex === index ? (
                    <ChevronUp className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  )}
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-6 bg-white">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Where We Work Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Where we work</h2>
          <p className="text-gray-600 mb-6">
            Green Frog Utilities delivers gas, water and electrical connections across England and Wales, from a single accredited contractor.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-1">
            {counties.map((county, index) => (
              <span key={index} className="text-sm text-gray-500">{county}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Accreditations;
